import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
    ArrowLeft, 
    Eye, 
    Volume2, 
    Play, 
    Pause, 
    Settings,
    RotateCcw,
    ChevronRight,
    Sparkles,
    Info,
    X,
    Check,
    Headphones,
    AlertCircle,
    Circle,
    Maximize2,
    Minimize2,
    Anchor,
    Brain,
    Zap
} from 'lucide-react';
import { toast } from 'sonner';
import { IconButton, PrimaryButton, SecondaryButton } from '../components/brand/BrandButton';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { Container } from '../components/brand/BrandLayout';
import { CoRegulateMark } from '../components/Logo';
import { ToolFeedback } from '../components/ToolFeedback';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============ CONFIGURATION ============

const MODES = {
    visual: { id: 'visual', name: 'Visual', icon: Eye, description: 'Follow a calming light to settle your mind' },
    audio: { id: 'audio', name: 'Audio', icon: Volume2, description: 'Gentle alternating tones to calm your nervous system', requiresHeadphones: true },
    combined: { id: 'combined', name: 'Combined', icon: Sparkles, description: 'Visual light synced with audio tones for deeper effect', requiresHeadphones: true, desktopOnly: true }
};

// Educational content about bilateral stimulation
const ABOUT_CONTENT = {
    title: "How EMDR/Bilateral Regulation Works",
    intro: "Bilateral stimulation uses rhythmic left-right sensory input to help calm your nervous system and quiet a busy mind.",
    sections: [
        {
            title: "What happens in your brain",
            content: "When you follow a moving light or listen to alternating tones, both hemispheres of your brain are engaged in a rhythmic, alternating pattern. This bilateral activation helps shift your nervous system from a 'fight or flight' state to a calmer, more regulated state."
        },
        {
            title: "Why it helps with ADHD",
            content: "ADHD brains often get stuck in loops of racing thoughts, worry spirals, or hyperactive states. The rhythmic bilateral movement gives your brain something predictable and calming to focus on, naturally interrupting these patterns and helping you feel more grounded."
        },
        {
            title: "The science",
            content: "Research shows bilateral stimulation activates the parasympathetic nervous system (your 'rest and digest' mode), reduces activity in the amygdala (your brain's alarm center), and can help with emotional regulation. The effect is similar to what happens during REM sleep, when your eyes naturally move side to side."
        },
        {
            title: "Best practices",
            content: "For most people, 1-4 minutes is enough to feel a shift. You don't need to 'try' to relax—just follow the movement and let your brain do the rest. If you feel overwhelmed at any point, simply pause and take a breath."
        }
    ],
    disclaimer: "This tool is for relaxation and grounding. It is not a substitute for therapy or treatment of trauma. For trauma processing, please work with a licensed EMDR therapist."
};

const VISUAL_PATTERNS = {
    horizontal: { id: 'horizontal', name: 'Horizontal', description: 'Side to side movement' },
    diagonal: { id: 'diagonal', name: 'Diagonal', description: 'Corner to corner' },
    figure8: { id: 'figure8', name: 'Figure 8', description: 'Infinity pattern' },
    circular: { id: 'circular', name: 'Circular', description: 'Around the edges' }
};

// Session categories - ADHD-appropriate speeds (60-120 BPM range)
// Based on clinical guidance: faster speeds help with focus and match ADHD's faster cognitive arousal
// 1.5-3 seconds per full pass (left-right-left) is optimal
// Each category has recommended defaults for duration, pattern, and tone
const SESSION_CATEGORIES = {
    calm: { 
        id: 'calm', 
        name: 'Calm & Ground', 
        icon: 'Anchor',
        duration: 2,      // 2 sec per pass = 60 BPM (lower end for grounding)
        bpm: 60, 
        description: 'Gentle pace to help you feel grounded and present',
        bestFor: 'Feeling scattered, need to slow down',
        // Recommended defaults for this category
        defaults: {
            targetCycles: 48,    // Standard ~2 min
            pattern: 'horizontal',
            audioTone: 'ocean',   // Ocean waves - most calming
            colorTheme: 'sage',
            orbSize: 'large'
        }
    },
    settle: { 
        id: 'settle', 
        name: 'Settle Racing Thoughts', 
        icon: 'Brain',
        duration: 1.5,    // 1.5 sec per pass = 80 BPM (standard for anxiety relief)
        bpm: 80, 
        description: 'Steady rhythm to quiet a busy mind',
        bestFor: 'Racing thoughts, mental chatter, spiraling',
        defaults: {
            targetCycles: 48,    // Standard ~2 min
            pattern: 'horizontal',
            audioTone: 'gentle',  // Gentle tone - not too stimulating
            colorTheme: 'sage',
            orbSize: 'medium'
        }
    },
    focus: { 
        id: 'focus', 
        name: 'Focus & Reset', 
        icon: 'Zap',
        duration: 1.2,    // 1.2 sec per pass = 100 BPM (faster for focus/stimulation)
        bpm: 100, 
        description: 'Engaging pace to sharpen attention and reset',
        bestFor: 'Need to refocus, feeling foggy or restless',
        defaults: {
            targetCycles: 24,    // Short ~1 min - quick reset
            pattern: 'figure8',   // More engaging pattern
            audioTone: 'click',   // Soft click - crisp, focusing
            colorTheme: 'ocean',
            orbSize: 'medium'
        }
    },
    energize: { 
        id: 'energize', 
        name: 'Energize & Activate', 
        icon: 'Sparkles',
        duration: 1,      // 1 sec per pass = 120 BPM (upper end for high arousal)
        bpm: 120, 
        description: 'Quick rhythm to boost alertness and engagement',
        bestFor: 'Low energy, need a cognitive boost',
        defaults: {
            targetCycles: 24,    // Short ~1 min
            pattern: 'figure8',   // Dynamic pattern
            audioTone: 'clear',   // Clear beep - energizing
            colorTheme: 'coral',
            orbSize: 'small'      // Faster tracking with smaller orb
        }
    }
};

// Keep SPEED_OPTIONS for backward compatibility with saved settings
const SPEED_OPTIONS = {
    calm: SESSION_CATEGORIES.calm,
    settle: SESSION_CATEGORIES.settle,
    focus: SESSION_CATEGORIES.focus,
    energize: SESSION_CATEGORIES.energize,
    // Map old values to new categories for backward compatibility
    verySlow: SESSION_CATEGORIES.calm,
    slow: SESSION_CATEGORIES.calm,
    medium: SESSION_CATEGORIES.settle,
    fast: SESSION_CATEGORIES.focus,
    veryFast: SESSION_CATEGORIES.energize
};

// Duration options - simplified for user-friendly selection
const DURATION_OPTIONS = [
    { sets: 2, cycles: 24, label: 'Short', time: '~1 min', description: 'A brief calming moment' },
    { sets: 4, cycles: 48, label: 'Standard', time: '~2 min', description: 'Recommended for most sessions' },
    { sets: 8, cycles: 96, label: 'Extended', time: '~4 min', description: 'Deeper settling' }
];

// Orb/dot size options
const SIZE_OPTIONS = {
    small: { id: 'small', name: 'Small', orbSize: 'w-8 h-8', glowSize: 'w-16 h-16' },
    medium: { id: 'medium', name: 'Medium', orbSize: 'w-12 h-12', glowSize: 'w-24 h-24' },
    large: { id: 'large', name: 'Large', orbSize: 'w-16 h-16', glowSize: 'w-32 h-32' },
    xlarge: { id: 'xlarge', name: 'Extra Large', orbSize: 'w-20 h-20', glowSize: 'w-40 h-40' }
};

// Audio tone options
const AUDIO_TONES = {
    gentle: { id: 'gentle', name: 'Gentle Tone', frequency: 432, type: 'sine', description: 'Soft, calming' },
    clear: { id: 'clear', name: 'Clear Beep', frequency: 528, type: 'sine', description: 'Clear, focused' },
    click: { id: 'click', name: 'Soft Click', isClick: true, description: 'Subtle click' },
    deep: { id: 'deep', name: 'Deep Tone', frequency: 256, type: 'sine', description: 'Grounding, low' },
    ocean: { id: 'ocean', name: 'Ocean Waves', isMusic: true, audioSrc: '/audio/ocean-peace.mp3', description: 'Gentle waves' },
    chime: { id: 'chime', name: 'Meditation Chime', isMusic: true, audioSrc: '/audio/meditation-chime.mp3', description: 'Peaceful bells' }
};

const COLOR_THEMES = {
    sage: { 
        id: 'sage', 
        name: 'Sage', 
        orbColor: '#1D6B6B',
        orb: 'from-[#1D6B6B] via-[#8FAF85] to-[#7D8E95]',
        glow: 'bg-[#7D8E95]/50',
        bg: 'bg-gradient-to-b from-[#F5F0E8] to-[#E8E4DC]'
    },
    ocean: { 
        id: 'ocean', 
        name: 'Ocean',
        orbColor: '#1E88E5',
        orb: 'from-[#1E88E5] via-[#64B5F6] to-[#90CAF9]',
        glow: 'bg-[#64B5F6]/50',
        bg: 'bg-gradient-to-b from-[#E3F2FD] to-[#BBDEFB]'
    },
    sunset: { 
        id: 'sunset', 
        name: 'Sunset',
        orbColor: '#FF7043',
        orb: 'from-[#FF7043] via-[#FFAB91] to-[#FFCCBC]',
        glow: 'bg-[#FFAB91]/50',
        bg: 'bg-gradient-to-b from-[#FFF3E0] to-[#FFE0B2]'
    },
    lavender: { 
        id: 'lavender', 
        name: 'Lavender',
        orbColor: '#7E57C2',
        orb: 'from-[#7E57C2] via-[#B39DDB] to-[#D1C4E9]',
        glow: 'bg-[#B39DDB]/50',
        bg: 'bg-gradient-to-b from-[#EDE7F6] to-[#D1C4E9]'
    },
    neutral: { 
        id: 'neutral', 
        name: 'Neutral',
        orbColor: '#607D8B',
        orb: 'from-[#607D8B] via-[#90A4AE] to-[#B0BEC5]',
        glow: 'bg-[#90A4AE]/50',
        bg: 'bg-gradient-to-b from-[#ECEFF1] to-[#CFD8DC]'
    }
};

// Guided prompts for processing sessions - Professional EMDR guidance
// Guided prompts for calming sessions - Focus on relaxation, grounding, NOT trauma processing
const GUIDED_PROMPTS = [
    "Let your mind settle as you follow the rhythm.",
    "Feel your body becoming more relaxed with each pass.",
    "Notice your breathing naturally slowing down.",
    "Let the gentle movement calm your busy mind.",
    "You don't need to think about anything right now.",
    "Just follow the movement. Nothing else matters.",
    "Feel the tension leaving your shoulders and jaw.",
    "Your thoughts are slowing down. That's perfect.",
    "Notice how your body feels more grounded.",
    "Each pass helps your nervous system settle.",
    "You're creating space between your thoughts.",
    "Let the rhythm gently interrupt any racing thoughts."
];

// Pre-session grounding instructions
const PRE_SESSION_INSTRUCTIONS = {
    visual: [
        "Find a comfortable position where you can see the screen clearly",
        "Keep your head still and follow the light with your eyes only",
        "Blink naturally - don't force your eyes to stay open",
        "If you feel overwhelmed, close your eyes briefly and take a breath"
    ],
    audio: [
        "Put on your headphones - this is required for bilateral effect",
        "Adjust the volume to a comfortable level",
        "Close your eyes and focus on the sound moving between your ears",
        "Let the alternating tones guide your attention from side to side"
    ],
    combined: [
        "Put on your headphones - audio and visual will be synced",
        "Keep your head still and follow the light with your eyes",
        "The tones will match the light's position - left ear when left, right ear when right",
        "This dual stimulation provides a deeper calming effect"
    ]
};

// Headphone requirement notice
const HEADPHONE_NOTICE = {
    title: "Headphones Required",
    message: "Audio bilateral stimulation only works with headphones or earbuds. The alternating left-right tones help your brain shift from an activated state to a calmer one.",
    tips: [
        "Use wired headphones for best results",
        "Wireless earbuds work too, but may have slight delay",
        "Make sure left/right channels are correct",
        "Disable any mono audio or audio enhancement settings"
    ]
};

// ============ AUDIO BLS COMPONENT (Professional Grade) ============

const AudioBLS = ({ isActive, speed, toneType = 'gentle', onCycleComplete, volume = 0.5 }) => {
    const audioContextRef = useRef(null);
    const oscillatorRef = useRef(null);
    const audioElementRef = useRef(null);
    const sourceNodeRef = useRef(null);
    const gainNodeRef = useRef(null);
    const pannerRef = useRef(null);
    const intervalRef = useRef(null);
    const [currentSide, setCurrentSide] = useState('left');
    const [passCount, setPassCount] = useState(0);

    const toneConfig = AUDIO_TONES[toneType] || AUDIO_TONES.gentle;
    const isMusic = toneConfig.isMusic;
    const isClick = toneConfig.isClick;

    useEffect(() => {
        if (!isActive) {
            // Cleanup
            if (oscillatorRef.current) {
                try { oscillatorRef.current.stop(); } catch (e) {}
                oscillatorRef.current = null;
            }
            if (audioElementRef.current) {
                audioElementRef.current.pause();
                audioElementRef.current = null;
            }
            if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                try { audioContextRef.current.close(); } catch (e) {}
                audioContextRef.current = null;
            }
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            return;
        }

        // Create audio context
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        audioContextRef.current = new AudioContext();

        if (isMusic) {
            // === MUSIC MODE ===
            // Load and play music file with bilateral panning
            audioElementRef.current = new Audio(toneConfig.audioSrc);
            audioElementRef.current.loop = true;
            audioElementRef.current.volume = volume;
            
            // Create nodes for panning
            sourceNodeRef.current = audioContextRef.current.createMediaElementSource(audioElementRef.current);
            pannerRef.current = audioContextRef.current.createStereoPanner();
            pannerRef.current.pan.setValueAtTime(-1, audioContextRef.current.currentTime);
            
            // Connect: source -> panner -> destination
            sourceNodeRef.current.connect(pannerRef.current);
            pannerRef.current.connect(audioContextRef.current.destination);
            
            // Start ocean track 6 seconds in to skip the intro (after metadata loads)
            if (toneType === 'ocean') {
                audioElementRef.current.addEventListener('loadedmetadata', () => {
                    if (audioElementRef.current) {
                        audioElementRef.current.currentTime = 6;
                    }
                }, { once: true });
            }
            
            audioElementRef.current.play().catch(console.error);

            // Calculate timing based on speed
            const speedConfig = SPEED_OPTIONS[speed] || SPEED_OPTIONS.settle;
            const halfCycleMs = (speedConfig.duration * 1000) / 2;
            
            let side = 'left';
            let passes = 0;

            intervalRef.current = setInterval(() => {
                side = side === 'left' ? 'right' : 'left';
                setCurrentSide(side);
                passes++;
                setPassCount(passes);
                
                if (pannerRef.current && audioContextRef.current) {
                    // Smooth pan transition for music
                    pannerRef.current.pan.setTargetAtTime(
                        side === 'left' ? -0.9 : 0.9, // Slightly less extreme for music
                        audioContextRef.current.currentTime,
                        0.1 // Smooth transition
                    );
                }

                if (side === 'left' && passes > 1) {
                    const completedCycles = Math.floor(passes / 2);
                    onCycleComplete?.(completedCycles);
                }
            }, halfCycleMs);

        } else if (isClick) {
            // === CLICK MODE ===
            // Create actual click sounds using noise burst with short envelope
            pannerRef.current = audioContextRef.current.createStereoPanner();
            pannerRef.current.pan.setValueAtTime(-1, audioContextRef.current.currentTime);
            pannerRef.current.connect(audioContextRef.current.destination);

            const speedConfig = SPEED_OPTIONS[speed] || SPEED_OPTIONS.settle;
            const halfCycleMs = (speedConfig.duration * 1000) / 2;
            
            let side = 'left';
            let passes = 0;

            // Function to create a click sound
            const playClick = () => {
                if (!audioContextRef.current) return;
                
                const ctx = audioContextRef.current;
                const now = ctx.currentTime;
                
                // Create a short noise burst for click
                const bufferSize = ctx.sampleRate * 0.015; // 15ms of noise
                const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
                const data = buffer.getChannelData(0);
                
                // Fill with filtered noise (softer click)
                for (let i = 0; i < bufferSize; i++) {
                    // Shaped noise - starts strong, fades quickly
                    const envelope = Math.exp(-i / (bufferSize * 0.2));
                    data[i] = (Math.random() * 2 - 1) * envelope * 0.5;
                }
                
                // Create buffer source
                const source = ctx.createBufferSource();
                source.buffer = buffer;
                
                // Create gain for volume control
                const clickGain = ctx.createGain();
                clickGain.gain.setValueAtTime(volume * 0.6, now);
                
                // Add a gentle low-pass filter to soften the click
                const filter = ctx.createBiquadFilter();
                filter.type = 'lowpass';
                filter.frequency.setValueAtTime(2000, now); // Soften high frequencies
                
                // Connect: source -> filter -> gain -> panner -> destination
                source.connect(filter);
                filter.connect(clickGain);
                clickGain.connect(pannerRef.current);
                
                source.start(now);
                source.stop(now + 0.02); // Stop after 20ms
            };

            intervalRef.current = setInterval(() => {
                side = side === 'left' ? 'right' : 'left';
                setCurrentSide(side);
                passes++;
                setPassCount(passes);
                
                if (pannerRef.current && audioContextRef.current) {
                    pannerRef.current.pan.setValueAtTime(
                        side === 'left' ? -1 : 1,
                        audioContextRef.current.currentTime
                    );
                    playClick();
                }

                if (side === 'left' && passes > 1) {
                    const completedCycles = Math.floor(passes / 2);
                    onCycleComplete?.(completedCycles);
                }
            }, halfCycleMs);

            // Play initial click
            playClick();

        } else {
            // === TONE MODE (Original) ===
            oscillatorRef.current = audioContextRef.current.createOscillator();
            oscillatorRef.current.type = toneConfig.type;
            oscillatorRef.current.frequency.setValueAtTime(toneConfig.frequency, audioContextRef.current.currentTime);
            
            gainNodeRef.current = audioContextRef.current.createGain();
            gainNodeRef.current.gain.setValueAtTime(0, audioContextRef.current.currentTime);
            
            pannerRef.current = audioContextRef.current.createStereoPanner();
            pannerRef.current.pan.setValueAtTime(-1, audioContextRef.current.currentTime);
            
            oscillatorRef.current.connect(gainNodeRef.current);
            gainNodeRef.current.connect(pannerRef.current);
            pannerRef.current.connect(audioContextRef.current.destination);
            
            oscillatorRef.current.start();

            const speedConfig = SPEED_OPTIONS[speed] || SPEED_OPTIONS.settle;
            const halfCycleMs = (speedConfig.duration * 1000) / 2;
            
            let side = 'left';
            let passes = 0;

            const playTone = () => {
                if (!gainNodeRef.current || !audioContextRef.current) return;
                
                const now = audioContextRef.current.currentTime;
                const attackTime = 0.02;
                const sustainTime = (halfCycleMs / 1000) * 0.6;
                const releaseTime = 0.05;
                
                gainNodeRef.current.gain.cancelScheduledValues(now);
                gainNodeRef.current.gain.setValueAtTime(0, now);
                gainNodeRef.current.gain.linearRampToValueAtTime(volume * 0.4, now + attackTime);
                gainNodeRef.current.gain.setValueAtTime(volume * 0.4, now + attackTime + sustainTime);
                gainNodeRef.current.gain.linearRampToValueAtTime(0, now + attackTime + sustainTime + releaseTime);
            };

            intervalRef.current = setInterval(() => {
                side = side === 'left' ? 'right' : 'left';
                setCurrentSide(side);
                passes++;
                setPassCount(passes);
                
                if (pannerRef.current && audioContextRef.current) {
                    pannerRef.current.pan.setValueAtTime(
                        side === 'left' ? -1 : 1,
                        audioContextRef.current.currentTime
                    );
                    playTone();
                }

                if (side === 'left' && passes > 1) {
                    const completedCycles = Math.floor(passes / 2);
                    onCycleComplete?.(completedCycles);
                }
            }, halfCycleMs);

            playTone();
        }

        return () => {
            if (oscillatorRef.current) {
                try { oscillatorRef.current.stop(); } catch (e) {}
                oscillatorRef.current = null;
            }
            if (audioElementRef.current) {
                audioElementRef.current.pause();
                audioElementRef.current = null;
            }
            if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                try { audioContextRef.current.close(); } catch (e) {}
                audioContextRef.current = null;
            }
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isActive, speed, toneType, volume, onCycleComplete, isMusic, isClick, toneConfig]);

    if (!isActive) return null;

    // Calculate the position of the orb based on currentSide
    // The orb should smoothly transition between left (0%) and right (100%)
    const orbPosition = currentSide === 'left' ? 0 : 100;
    const transitionDuration = ((SPEED_OPTIONS[speed]?.duration || 3) / 2);

    return (
        <div className="flex flex-col items-center justify-center py-12">
            {/* Audio visualization - Professional style */}
            <div className="relative w-80 h-40 flex items-center justify-center">
                {/* Connection arc */}
                <svg className="absolute inset-0 w-full h-full" viewBox="0 0 320 160">
                    <path 
                        d="M40,80 Q160,20 280,80" 
                        fill="none" 
                        stroke="rgba(255,255,255,0.1)" 
                        strokeWidth="2"
                        strokeDasharray="4,4"
                    />
                </svg>
                
                {/* Moving orb indicator - synced with audio */}
                <div 
                    className="absolute top-4 w-4 h-4 rounded-full bg-[#FFBB98] shadow-lg shadow-[#FFBB98]/50"
                    style={{
                        left: `calc(${orbPosition}% - 8px)`,
                        marginLeft: orbPosition === 0 ? '40px' : '-40px',
                        transition: `left ${transitionDuration}s ease-in-out, margin-left ${transitionDuration}s ease-in-out`
                    }}
                />

                {/* Left ear indicator */}
                <div className={cn(
                    'absolute left-0 w-20 h-20 rounded-full flex flex-col items-center justify-center transition-all duration-150',
                    currentSide === 'left' 
                        ? 'bg-[#1D6B6B] scale-110 shadow-lg shadow-[#1D6B6B]/50' 
                        : 'bg-white/10 scale-100'
                )}>
                    <Volume2 className={cn(
                        'w-8 h-8 mb-1 transition-colors',
                        currentSide === 'left' ? 'text-white' : 'text-white/40'
                    )} />
                    <span className={cn(
                        'text-xs font-medium',
                        currentSide === 'left' ? 'text-white' : 'text-white/40'
                    )}>LEFT</span>
                </div>

                {/* Center - Pass counter */}
                <div className="flex flex-col items-center">
                    <span className="text-3xl font-light text-white/80">{passCount}</span>
                    <span className="text-xs text-white/40">passes</span>
                </div>

                {/* Right ear indicator */}
                <div className={cn(
                    'absolute right-0 w-20 h-20 rounded-full flex flex-col items-center justify-center transition-all duration-150',
                    currentSide === 'right' 
                        ? 'bg-[#1D6B6B] scale-110 shadow-lg shadow-[#1D6B6B]/50' 
                        : 'bg-white/10 scale-100'
                )}>
                    <Volume2 className={cn(
                        'w-8 h-8 mb-1 transition-colors',
                        currentSide === 'right' ? 'text-white' : 'text-white/40'
                    )} />
                    <span className={cn(
                        'text-xs font-medium',
                        currentSide === 'right' ? 'text-white' : 'text-white/40'
                    )}>RIGHT</span>
                </div>
            </div>

            {/* Headphones reminder */}
            <div className="flex items-center gap-2 mt-8 px-4 py-2 rounded-full bg-white/10">
                <Headphones className="w-4 h-4 text-[#FFBB98]" />
                <SmallText className="text-white/60">Headphones active</SmallText>
            </div>
        </div>
    );
};

// ============ SYNCED AUDIO COMPONENT (For Combined Mode) ============
// This component receives position from the visual orb and syncs audio panning

const SyncedAudio = ({ isActive, orbPosition, speed, toneType = 'gentle', volume = 0.5 }) => {
    const audioContextRef = useRef(null);
    const oscillatorRef = useRef(null);
    const audioElementRef = useRef(null);
    const sourceNodeRef = useRef(null);
    const gainNodeRef = useRef(null);
    const pannerRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);

    const toneConfig = AUDIO_TONES[toneType] || AUDIO_TONES.gentle;
    const isMusic = toneConfig.isMusic;
    const isClick = toneConfig.isClick;

    // Initialize audio context
    useEffect(() => {
        if (!isActive) {
            if (oscillatorRef.current) {
                try { oscillatorRef.current.stop(); } catch (e) {}
                oscillatorRef.current = null;
            }
            if (audioElementRef.current) {
                audioElementRef.current.pause();
                audioElementRef.current = null;
            }
            if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                try { audioContextRef.current.close(); } catch (e) {}
                audioContextRef.current = null;
            }
            setIsPlaying(false);
            return;
        }

        // Create audio context
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        audioContextRef.current = new AudioContext();
        
        // Create stereo panner for bilateral effect
        pannerRef.current = audioContextRef.current.createStereoPanner();
        pannerRef.current.pan.setValueAtTime(0, audioContextRef.current.currentTime);
        pannerRef.current.connect(audioContextRef.current.destination);

        if (isMusic) {
            // === MUSIC MODE ===
            audioElementRef.current = new Audio(toneConfig.audioSrc);
            audioElementRef.current.loop = true;
            audioElementRef.current.volume = volume;
            
            sourceNodeRef.current = audioContextRef.current.createMediaElementSource(audioElementRef.current);
            sourceNodeRef.current.connect(pannerRef.current);
            
            // Start ocean track 6 seconds in to skip the intro (after metadata loads)
            if (toneType === 'ocean') {
                audioElementRef.current.addEventListener('loadedmetadata', () => {
                    if (audioElementRef.current) {
                        audioElementRef.current.currentTime = 6;
                    }
                }, { once: true });
            }
            
            audioElementRef.current.play().catch(console.error);
            setIsPlaying(true);
        } else if (!isClick) {
            // === TONE MODE (not click - click doesn't work well with continuous play) ===
            oscillatorRef.current = audioContextRef.current.createOscillator();
            oscillatorRef.current.type = toneConfig.type || 'sine';
            oscillatorRef.current.frequency.setValueAtTime(toneConfig.frequency || 432, audioContextRef.current.currentTime);
            
            gainNodeRef.current = audioContextRef.current.createGain();
            gainNodeRef.current.gain.setValueAtTime(volume * 0.3, audioContextRef.current.currentTime);
            
            oscillatorRef.current.connect(gainNodeRef.current);
            gainNodeRef.current.connect(pannerRef.current);
            
            oscillatorRef.current.start();
            setIsPlaying(true);
        } else {
            // Click mode doesn't make sense for continuous synced audio, use gentle tone instead
            oscillatorRef.current = audioContextRef.current.createOscillator();
            oscillatorRef.current.type = 'sine';
            oscillatorRef.current.frequency.setValueAtTime(432, audioContextRef.current.currentTime);
            
            gainNodeRef.current = audioContextRef.current.createGain();
            gainNodeRef.current.gain.setValueAtTime(volume * 0.3, audioContextRef.current.currentTime);
            
            oscillatorRef.current.connect(gainNodeRef.current);
            gainNodeRef.current.connect(pannerRef.current);
            
            oscillatorRef.current.start();
            setIsPlaying(true);
        }

        return () => {
            if (oscillatorRef.current) {
                try { oscillatorRef.current.stop(); } catch (e) {}
                oscillatorRef.current = null;
            }
            if (audioElementRef.current) {
                audioElementRef.current.pause();
                audioElementRef.current = null;
            }
            if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                try { audioContextRef.current.close(); } catch (e) {}
                audioContextRef.current = null;
            }
        };
    }, [isActive, toneType, volume, isMusic, isClick, toneConfig]);

    // Sync panning with orb position
    useEffect(() => {
        if (!isPlaying || !pannerRef.current || !audioContextRef.current || !orbPosition) return;
        
        // Convert orb X position (0-100) to pan value (-1 to 1)
        // 0% = full left (-1), 50% = center (0), 100% = full right (1)
        const panValue = (orbPosition.x / 50) - 1; // Maps 0-100 to -1 to 1
        
        // Smooth panning transition
        pannerRef.current.pan.setTargetAtTime(
            Math.max(-1, Math.min(1, panValue)), 
            audioContextRef.current.currentTime, 
            0.05 // 50ms smoothing
        );
    }, [isPlaying, orbPosition]);

    // Update volume
    useEffect(() => {
        if (gainNodeRef.current && audioContextRef.current) {
            gainNodeRef.current.gain.setTargetAtTime(
                volume * 0.3, 
                audioContextRef.current.currentTime, 
                0.1
            );
        }
        if (audioElementRef.current) {
            audioElementRef.current.volume = volume;
        }
    }, [volume]);

    return null; // This component doesn't render anything visible
};

// ============ VISUAL BLS COMPONENT (ENHANCED) ============

const VisualBLSEnhanced = ({ 
    isActive, 
    pattern, 
    speed, 
    colorTheme,
    orbSize = 'medium',
    onCycleComplete,
    onPositionChange, // New prop for combined mode - reports position for audio sync
}) => {
    const [position, setPosition] = useState({ x: 50, y: 50 });
    const [currentCycle, setCurrentCycle] = useState(0);
    const [passCount, setPassCount] = useState(0);
    const animationRef = useRef(null);
    const startTimeRef = useRef(null);
    const lastDirectionRef = useRef('right');

    const theme = COLOR_THEMES[colorTheme] || COLOR_THEMES.sage;
    const speedConfig = SPEED_OPTIONS[speed] || SPEED_OPTIONS.medium;
    const sizeConfig = SIZE_OPTIONS[orbSize] || SIZE_OPTIONS.medium;
    const cycleDuration = speedConfig.duration * 1000;

    // Pattern calculations - smooth easing for professional feel
    // Maximized range for wider eye movement (more effective bilateral stimulation)
    const getPosition = useCallback((progress, patternType) => {
        // Use sine easing for smooth movement
        const easeProgress = (Math.sin((progress - 0.25) * Math.PI * 2) + 1) / 2;
        
        switch (patternType) {
            case 'horizontal':
                return {
                    x: 2 + easeProgress * 96, // 2% to 98% - maximize horizontal range
                    y: 50
                };
            case 'diagonal':
                return {
                    x: 2 + easeProgress * 96,
                    y: 15 + easeProgress * 70
                };
            case 'figure8':
                const t = progress * Math.PI * 2;
                return {
                    x: 50 + Math.sin(t) * 47, // Wider figure-8
                    y: 50 + Math.sin(t * 2) * 35
                };
            case 'circular':
                const angle = progress * Math.PI * 2;
                return {
                    x: 50 + Math.cos(angle) * 46, // Wider circle
                    y: 50 + Math.sin(angle) * 38
                };
            default:
                return { x: 50, y: 50 };
        }
    }, []);

    useEffect(() => {
        if (!isActive) {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
            return;
        }

        startTimeRef.current = performance.now();
        let lastCycle = 0;
        let passes = 0;

        const animate = (currentTime) => {
            const elapsed = currentTime - startTimeRef.current;
            const cycleProgress = (elapsed % cycleDuration) / cycleDuration;
            const totalCycles = Math.floor(elapsed / cycleDuration);

            // Update position
            const newPos = getPosition(cycleProgress, pattern);
            setPosition(newPos);
            
            // Report position change for combined mode audio sync
            onPositionChange?.(newPos);

            // Track direction changes for pass counting (horizontal pattern)
            const currentDirection = cycleProgress < 0.5 ? 'right' : 'left';
            if (currentDirection !== lastDirectionRef.current) {
                lastDirectionRef.current = currentDirection;
                passes++;
                setPassCount(passes);
            }

            // Track cycles
            if (totalCycles > lastCycle) {
                lastCycle = totalCycles;
                setCurrentCycle(totalCycles);
                onCycleComplete?.(totalCycles);
            }

            animationRef.current = requestAnimationFrame(animate);
        };

        animationRef.current = requestAnimationFrame(animate);

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [isActive, pattern, speed, cycleDuration, getPosition, onCycleComplete]);

    return (
        <div 
            className={cn(
                'relative rounded-2xl flex items-center justify-center overflow-hidden',
                // Maximize screen usage - nearly full width, taller for better eye movement
                'w-[98vw] max-w-[1400px] h-[50vh] min-h-[300px] max-h-[500px]',
                // On mobile, use even more of the screen
                'sm:h-[45vh] sm:min-h-[280px]',
                theme.bg
            )}
            data-testid="visual-bls-enhanced"
        >
            {/* Track border - subtle */}
            <div className="absolute inset-0 rounded-2xl border border-[#7D8E95]/5" />

            {/* Pattern guide lines (very subtle) */}
            {pattern === 'horizontal' && (
                <div className="absolute w-[94%] h-0.5 bg-[#7D8E95]/5" />
            )}
            {pattern === 'diagonal' && (
                <div className="absolute w-[85%] h-0.5 bg-[#7D8E95]/5 rotate-[15deg]" />
            )}
            {pattern === 'figure8' && (
                <svg className="absolute inset-0 w-full h-full opacity-5" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <path 
                        d="M5,50 C5,20 50,20 50,50 C50,80 95,80 95,50 C95,20 50,20 50,50 C50,80 5,80 5,50" 
                        fill="none" 
                        stroke="#7D8E95" 
                        strokeWidth="0.3"
                    />
                </svg>
            )}
            {pattern === 'circular' && (
                <div className="absolute w-[90%] h-[80%] border border-[#7D8E95]/5 rounded-full" />
            )}

            {/* Pass counter - minimized, top corner */}
            <div className="absolute top-3 right-4 flex items-center gap-1 text-[#7D8E95]/40">
                <span className="text-lg font-light">{passCount}</span>
                <span className="text-xs">passes</span>
            </div>

            {/* Moving orb */}
            <div 
                className="absolute"
                style={{
                    left: `${position.x}%`,
                    top: `${position.y}%`,
                    transform: 'translate(-50%, -50%)',
                    transition: 'none'
                }}
            >
                {/* Outer glow */}
                <div className={cn(
                    'absolute rounded-full blur-2xl opacity-30 -inset-6',
                    sizeConfig.glowSize,
                    theme.glow
                )} />
                {/* Inner glow */}
                <div className={cn(
                    'absolute rounded-full blur-xl opacity-50 -inset-3',
                    theme.glow
                )} />
                {/* Main orb - solid color for better tracking */}
                <div 
                    className={cn(
                        'relative rounded-full shadow-2xl',
                        sizeConfig.orbSize
                    )}
                    style={{ 
                        backgroundColor: theme.orbColor,
                        boxShadow: `0 0 30px ${theme.orbColor}50, 0 0 60px ${theme.orbColor}30`
                    }}
                />
            </div>

            {/* Edge markers - subtle indicators for eye movement range */}
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-0.5 h-6 rounded-full bg-[#7D8E95]/15" />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 w-0.5 h-6 rounded-full bg-[#7D8E95]/15" />
        </div>
    );
};

// ============ MAIN EMDR COMPONENT ============

const EMDR = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    // Session state
    const [sessionPhase, setSessionPhase] = useState('setup'); // 'setup' | 'pre-session' | 'active' | 'complete'
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentCycle, setCurrentCycle] = useState(0);
    const [showFeedback, setShowFeedback] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    const [showHeadphoneWarning, setShowHeadphoneWarning] = useState(false);
    const [headphoneConfirmed, setHeadphoneConfirmed] = useState(false);
    const [currentPrompt, setCurrentPrompt] = useState(null);
    const [promptIndex, setPromptIndex] = useState(0);
    const [isMobile, setIsMobile] = useState(false);
    const [showMobileNotice, setShowMobileNotice] = useState(false);
    const [isDesktop, setIsDesktop] = useState(false);
    const [showLearnMore, setShowLearnMore] = useState(false);

    // Settings state
    const [mode, setMode] = useState('visual');
    const [pattern, setPattern] = useState('horizontal');
    const [speed, setSpeed] = useState('settle'); // Default to "Settle Racing Thoughts" - 80 BPM
    const [targetCycles, setTargetCycles] = useState(48); // Default "Standard" ~2 min
    const [colorTheme, setColorTheme] = useState('sage');
    const [guidedMode, setGuidedMode] = useState(true); // Default on
    const [audioVolume, setAudioVolume] = useState(0.5);
    const [orbSize, setOrbSize] = useState('medium');
    const [audioTone, setAudioTone] = useState('gentle');
    const [showCustomize, setShowCustomize] = useState(false); // Toggle for customization panel
    
    // Combined mode - track orb position for synced audio
    const [orbPosition, setOrbPosition] = useState({ x: 50, y: 50 });

    // Apply recommended defaults when category changes
    const applyDefaultsForCategory = (categoryId) => {
        const category = SESSION_CATEGORIES[categoryId];
        if (category?.defaults) {
            const d = category.defaults;
            setTargetCycles(d.targetCycles);
            setPattern(d.pattern);
            setAudioTone(d.audioTone);
            setColorTheme(d.colorTheme);
            setOrbSize(d.orbSize);
        }
    };

    // Handle category selection - apply defaults
    const handleCategorySelect = (categoryId) => {
        setSpeed(categoryId);
        applyDefaultsForCategory(categoryId);
        setShowCustomize(false); // Reset customization panel
    };

    // Detect mobile/desktop device and set appropriate defaults
    useEffect(() => {
        const checkDevice = () => {
            const isMobileDevice = window.innerWidth < 768 || 
                /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            const isDesktopDevice = window.innerWidth >= 1024 && !isMobileDevice;
            setIsMobile(isMobileDevice);
            setIsDesktop(isDesktopDevice);
            return { isMobileDevice, isDesktopDevice };
        };
        
        const { isMobileDevice } = checkDevice();
        
        // Check if user has saved settings - if not, default to audio on mobile
        const savedSettings = localStorage.getItem('coregulate_emdr_settings');
        if (!savedSettings && isMobileDevice) {
            setMode('audio');
            setShowMobileNotice(true); // Show notice explaining why audio is recommended
        }
        
        // Listen for resize events
        window.addEventListener('resize', checkDevice);
        return () => window.removeEventListener('resize', checkDevice);
    }, []);

    // Load settings from URL params or localStorage
    useEffect(() => {
        const savedSettings = localStorage.getItem('coregulate_emdr_settings');
        if (savedSettings) {
            try {
                const parsed = JSON.parse(savedSettings);
                if (parsed.mode) setMode(parsed.mode);
                if (parsed.pattern) setPattern(parsed.pattern);
                if (parsed.speed) setSpeed(parsed.speed);
                if (parsed.targetCycles) setTargetCycles(parsed.targetCycles);
                if (parsed.colorTheme) setColorTheme(parsed.colorTheme);
                if (parsed.guidedMode !== undefined) setGuidedMode(parsed.guidedMode);
                if (parsed.orbSize) setOrbSize(parsed.orbSize);
                if (parsed.audioTone) setAudioTone(parsed.audioTone);
                if (parsed.headphoneConfirmed) setHeadphoneConfirmed(parsed.headphoneConfirmed);
            } catch (e) {
                console.error('Failed to load EMDR settings:', e);
            }
        }

        // URL params override
        const urlMode = searchParams.get('mode');
        const urlPattern = searchParams.get('pattern');
        if (urlMode && MODES[urlMode]) setMode(urlMode);
        if (urlPattern && VISUAL_PATTERNS[urlPattern]) setPattern(urlPattern);
    }, [searchParams]);

    // Save settings
    const saveSettings = useCallback(() => {
        localStorage.setItem('coregulate_emdr_settings', JSON.stringify({
            mode, pattern, speed, targetCycles, colorTheme, guidedMode, orbSize, audioTone, headphoneConfirmed
        }));
    }, [mode, pattern, speed, targetCycles, colorTheme, guidedMode, orbSize, audioTone, headphoneConfirmed]);

    // Guided prompts rotation
    useEffect(() => {
        if (!isPlaying || !guidedMode) return;

        const interval = setInterval(() => {
            setPromptIndex(prev => {
                const next = (prev + 1) % GUIDED_PROMPTS.length;
                setCurrentPrompt(GUIDED_PROMPTS[next]);
                return next;
            });
        }, 10000); // New prompt every 10 seconds

        // Show first prompt immediately
        setCurrentPrompt(GUIDED_PROMPTS[promptIndex]);

        return () => clearInterval(interval);
    }, [isPlaying, guidedMode, promptIndex]);

    // Handle cycle completion
    const handleCycleComplete = useCallback((cycles) => {
        setCurrentCycle(cycles);
        
        if (cycles >= targetCycles) {
            setIsPlaying(false);
            setSessionPhase('complete');
            
            // Track usage
            const token = localStorage.getItem('co_regulate_token');
            fetch(`${API_URL}/api/tool-feedback`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { 'Authorization': `Bearer ${token}` })
                },
                body: JSON.stringify({
                    tool_id: `emdr-${mode}-${pattern}`,
                    tool_name: `EMDR/Bilateral Regulation (${mode === 'audio' ? 'Audio' : mode === 'combined' ? 'Combined' : VISUAL_PATTERNS[pattern]?.name})`,
                    mood_rating: 0, // Will be updated by feedback
                    mood_label: 'Session completed',
                    timestamp: new Date().toISOString()
                })
            }).catch(console.error);
        }
    }, [targetCycles, mode, pattern]);

    // Start session
    const handleStart = () => {
        saveSettings();
        setSessionPhase('active');
        setCurrentCycle(0);
        setIsPlaying(true);
    };

    // Toggle play/pause
    const handleTogglePlay = () => {
        setIsPlaying(prev => !prev);
    };

    // Reset session
    const handleReset = () => {
        setIsPlaying(false); // Stop audio/visual first
        setCurrentCycle(0);
        // Small delay to allow cleanup effects to run before unmounting
        setTimeout(() => {
            setSessionPhase('setup');
        }, 100);
    };

    // Complete and show feedback
    const handleComplete = () => {
        setIsPlaying(false); // Stop the audio/visual first
        setShowFeedback(true);
    };

    // After feedback
    const handleFeedbackClose = () => {
        setShowFeedback(false);
        setSessionPhase('setup');
        setCurrentCycle(0);
    };

    // ============ RENDER: SETUP PHASE ============
    if (sessionPhase === 'setup') {
        return (
            <div className="min-h-screen bg-[#F5F0E8]">
                {/* Header */}
                <header className="sticky top-0 z-50 glass-header">
                    <Container size="sm" className="py-5 flex items-center justify-between">
                        <IconButton 
                            onClick={() => navigate('/regulate')}
                            data-testid="back-btn"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </IconButton>
                        <Heading as="h1" size="text-lg">EMDR/Bilateral Regulation</Heading>
                        <IconButton onClick={() => setShowInfo(true)} data-testid="info-btn">
                            <Info className="w-5 h-5" />
                        </IconButton>
                    </Container>
                </header>

                <main className="max-w-xl mx-auto px-6 py-8">
                    {/* Mobile Notice Banner */}
                    {isMobile && showMobileNotice && (
                        <div className="mb-6 p-4 rounded-2xl bg-[#1D6B6B]/10 border border-[#1D6B6B]/20">
                            <div className="flex items-start gap-3">
                                <div className="w-8 h-8 rounded-lg bg-[#1D6B6B] flex items-center justify-center flex-shrink-0">
                                    <Headphones className="w-4 h-4 text-white" />
                                </div>
                                <div className="flex-1">
                                    <BodyText className="font-medium text-[#1D6B6B] mb-1">
                                        Audio Mode Recommended
                                    </BodyText>
                                    <SmallText className="text-[#7D8E95]">
                                        On mobile devices, audio bilateral stimulation is more effective than visual. 
                                        The limited screen size reduces the eye movement range needed for visual EMDR.
                                    </SmallText>
                                </div>
                                <button 
                                    onClick={() => setShowMobileNotice(false)}
                                    className="text-[#7D8E95] hover:text-[#1D6B6B] p-1"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Learn More - Educational Content */}
                    <section className="mb-8">
                        <div className="rounded-2xl border border-[#1D6B6B]/20 overflow-hidden">
                            {/* Always visible header with intro */}
                            <div className="p-4 bg-[#1D6B6B]/5">
                                <div className="flex items-start gap-3 mb-3">
                                    <div className="w-10 h-10 rounded-xl bg-[#1D6B6B] flex items-center justify-center flex-shrink-0">
                                        <Info className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <Heading as="h3" size="text-base" className="text-[#344648] mb-1">
                                            {ABOUT_CONTENT.title}
                                        </Heading>
                                        <BodyText className="text-[#7D8E95] leading-relaxed">
                                            {ABOUT_CONTENT.intro}
                                        </BodyText>
                                    </div>
                                </div>
                                
                                {/* Expand button */}
                                <button
                                    onClick={() => setShowLearnMore(!showLearnMore)}
                                    className="w-full mt-2 py-2 px-3 rounded-xl bg-white/60 hover:bg-white/80 transition-colors flex items-center justify-center gap-2"
                                    data-testid="learn-more-toggle"
                                >
                                    <SmallText className="font-medium text-[#1D6B6B]">
                                        {showLearnMore ? 'Show less' : 'Learn more about the science'}
                                    </SmallText>
                                    <ChevronRight className={cn(
                                        'w-4 h-4 text-[#1D6B6B] transition-transform',
                                        showLearnMore && 'rotate-90'
                                    )} />
                                </button>
                            </div>
                            
                            {/* Expandable details */}
                            {showLearnMore && (
                                <div className="p-4 bg-white/50 border-t border-[#1D6B6B]/10 animate-in slide-in-from-top-2 duration-200">
                                    <div className="space-y-5">
                                        {ABOUT_CONTENT.sections.map((section, i) => (
                                            <div key={i}>
                                                <BodyText className="font-semibold text-[#344648] mb-2">
                                                    {section.title}
                                                </BodyText>
                                                <BodyText className="text-[#7D8E95] leading-relaxed">
                                                    {section.content}
                                                </BodyText>
                                            </div>
                                        ))}
                                    </div>
                                    
                                    {/* Disclaimer */}
                                    <div className="mt-5 p-4 rounded-xl bg-[#FFBB98]/10 border border-white/40/20">
                                        <BodyText className="text-[#7D8E95] text-sm leading-relaxed">
                                            <span className="font-semibold text-[#E09060]">Important: </span>
                                            {ABOUT_CONTENT.disclaimer}
                                        </BodyText>
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Mode Selection */}
                    <section className="mb-8">
                        <SmallText className="text-[#7D8E95] uppercase tracking-wide mb-3">Mode</SmallText>
                        <div className={cn(
                            "grid gap-3",
                            isDesktop ? "grid-cols-3" : "grid-cols-2"
                        )}>
                            {Object.values(MODES).map((m) => {
                                // Hide combined mode on mobile/tablet
                                if (m.desktopOnly && !isDesktop) return null;
                                
                                const Icon = m.icon;
                                const isSelected = mode === m.id;
                                const isRecommendedOnMobile = isMobile && m.id === 'audio';
                                const isDesktopExclusive = m.desktopOnly;
                                return (
                                    <button
                                        key={m.id}
                                        onClick={() => setMode(m.id)}
                                        className={cn(
                                            'p-4 rounded-2xl border-2 transition-all text-left relative',
                                            isSelected 
                                                ? 'border-[#1D6B6B] bg-[#1D6B6B]/10' 
                                                : 'border-[#7D8E95]/20 bg-white/50 hover:border-[#7D8E95]/40'
                                        )}
                                        data-testid={`mode-${m.id}`}
                                    >
                                        {/* Mobile Recommended Badge */}
                                        {isRecommendedOnMobile && (
                                            <div className="absolute -top-2 -right-2 px-2 py-0.5 bg-[#1D6B6B] text-white text-xs font-medium rounded-full">
                                                Recommended
                                            </div>
                                        )}
                                        {/* Desktop Badge for Visual and Combined modes */}
                                        {(m.id === 'visual' || isDesktopExclusive) && !isRecommendedOnMobile && (
                                            <div className="absolute -top-2 -right-2 px-2 py-0.5 bg-[#FFBB98] text-white text-xs font-medium rounded-full">
                                                Desktop
                                            </div>
                                        )}
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className={cn(
                                                'w-10 h-10 rounded-xl flex items-center justify-center',
                                                isSelected ? 'bg-[#1D6B6B]' : 'bg-[#7D8E95]/20'
                                            )}>
                                                <Icon className={cn('w-5 h-5', isSelected ? 'text-white' : 'text-[#7D8E95]')} />
                                            </div>
                                            <BodyText className={cn('font-medium', isSelected ? 'text-[#1D6B6B]' : 'text-[#7D8E95]')}>
                                                {m.name}
                                            </BodyText>
                                        </div>
                                        <SmallText className="text-[#7D8E95]">{m.description}</SmallText>
                                    </button>
                                );
                            })}
                        </div>
                    </section>

                    {/* Mobile Warning for Visual Mode */}
                    {isMobile && mode === 'visual' && (
                        <section className="mb-6">
                            <div className="p-3 rounded-xl bg-[#FFBB98]/20 border border-white/40/30">
                                <div className="flex items-center gap-2">
                                    <AlertCircle className="w-4 h-4 text-[#E09060]" />
                                    <SmallText className="text-[#7D8E95]">
                                        <span className="font-medium">Limited effectiveness on mobile.</span> For best results, use audio mode with headphones or switch to a larger screen.
                                    </SmallText>
                                </div>
                            </div>
                        </section>
                    )}

                    {/* Visual Pattern Selection (only if visual or combined mode) */}
                    {(mode === 'visual' || mode === 'combined') && (
                        <section className="mb-8">
                            <SmallText className="text-[#7D8E95] uppercase tracking-wide mb-3">Pattern</SmallText>
                            <div className="grid grid-cols-2 gap-3">
                                {Object.values(VISUAL_PATTERNS).map((p) => {
                                    const isSelected = pattern === p.id;
                                    return (
                                        <button
                                            key={p.id}
                                            onClick={() => setPattern(p.id)}
                                            className={cn(
                                                'p-3 rounded-xl border-2 transition-all',
                                                isSelected 
                                                    ? 'border-[#1D6B6B] bg-[#1D6B6B]/10' 
                                                    : 'border-[#7D8E95]/20 bg-white/50 hover:border-[#7D8E95]/40'
                                            )}
                                            data-testid={`pattern-${p.id}`}
                                        >
                                            <BodyText className={cn('font-medium', isSelected ? 'text-[#1D6B6B]' : 'text-[#7D8E95]')}>
                                                {p.name}
                                            </BodyText>
                                            <SmallText className="text-[#7D8E95] text-xs">{p.description}</SmallText>
                                        </button>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    {/* Headphone Notice (audio and combined modes) */}
                    {(mode === 'audio' || mode === 'combined') && (
                        <section className="mb-8">
                            <div className={cn(
                                'p-4 rounded-2xl border-2 transition-all',
                                headphoneConfirmed 
                                    ? 'border-[#1D6B6B]/30 bg-[#1D6B6B]/10' 
                                    : 'border-white/40/50 bg-[#FFBB98]/10'
                            )}>
                                <div className="flex items-start gap-3">
                                    <div className={cn(
                                        'w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0',
                                        headphoneConfirmed ? 'bg-[#1D6B6B]' : 'bg-[#FFBB98]'
                                    )}>
                                        <Headphones className="w-5 h-5 text-white" />
                                    </div>
                                    <div className="flex-1">
                                        <BodyText className={cn(
                                            'font-medium mb-1',
                                            headphoneConfirmed ? 'text-[#1D6B6B]' : 'text-[#7D8E95]'
                                        )}>
                                            {headphoneConfirmed ? 'Headphones Needed' : 'Headphones Required'}
                                        </BodyText>
                                        <SmallText className="text-[#7D8E95]">
                                            {headphoneConfirmed 
                                                ? 'If you have your headphones ready, you\'re all set for bilateral audio stimulation.'
                                                : 'Stereo headphones or earbuds are needed for the bilateral effect. You\'ll be prompted when you start.'
                                            }
                                        </SmallText>
                                    </div>
                                </div>
                            </div>
                        </section>
                    )}

                    {/* Session Type Selection - Category-based for ADHD needs */}
                    <section className="mb-6">
                        <SmallText className="text-[#7D8E95] uppercase tracking-wide mb-3">What do you need?</SmallText>
                        <div className="grid grid-cols-1 gap-3">
                            {Object.values(SESSION_CATEGORIES).map((cat) => {
                                const isSelected = speed === cat.id;
                                const IconComponent = cat.icon === 'Anchor' ? Anchor : 
                                                     cat.icon === 'Brain' ? Brain : 
                                                     cat.icon === 'Zap' ? Zap : Sparkles;
                                return (
                                    <div
                                        key={cat.id}
                                        className={cn(
                                            'p-4 rounded-2xl border-2 transition-all',
                                            isSelected 
                                                ? 'border-[#1D6B6B] bg-[#1D6B6B]/10' 
                                                : 'border-[#7D8E95]/20 bg-white/50 hover:border-[#7D8E95]/40'
                                        )}
                                        data-testid={`category-${cat.id}`}
                                    >
                                        <button
                                            onClick={() => handleCategorySelect(cat.id)}
                                            className="w-full text-left"
                                        >
                                            <div className="flex items-start gap-3">
                                                <div className={cn(
                                                    'w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0',
                                                    isSelected ? 'bg-[#1D6B6B]' : 'bg-[#7D8E95]/20'
                                                )}>
                                                    <IconComponent className={cn('w-5 h-5', isSelected ? 'text-white' : 'text-[#7D8E95]')} />
                                                </div>
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <BodyText className={cn('font-medium', isSelected ? 'text-[#1D6B6B]' : 'text-[#344648]')}>
                                                            {cat.name}
                                                        </BodyText>
                                                        <span className="text-xs text-[#7D8E95]/60">{cat.bpm} BPM</span>
                                                    </div>
                                                    <SmallText className="text-[#7D8E95] mt-0.5">{cat.description}</SmallText>
                                                    <SmallText className="text-[#7D8E95]/70 text-xs mt-1 italic">Best for: {cat.bestFor}</SmallText>
                                                </div>
                                            </div>
                                        </button>
                                        
                                        {/* Customize button - appears on all cards */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                // Select this category first if not already selected
                                                if (!isSelected) {
                                                    handleCategorySelect(cat.id);
                                                }
                                                setShowCustomize(!showCustomize);
                                            }}
                                            className={cn(
                                                "mt-3 w-full py-2 px-3 rounded-xl flex items-center justify-center gap-2 transition-all",
                                                isSelected && showCustomize 
                                                    ? "bg-[#1D6B6B] text-white" 
                                                    : "bg-white/60 hover:bg-white/80 text-[#1D6B6B] border border-[#1D6B6B]/20"
                                            )}
                                            data-testid={`customize-${cat.id}`}
                                        >
                                            <Settings className="w-4 h-4" />
                                            <SmallText className="font-medium">
                                                {isSelected && showCustomize ? 'Hide options' : 'Customize'}
                                            </SmallText>
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </section>

                    {/* Customization Options - Collapsible */}
                    {showCustomize && (
                        <div className="mb-6 p-4 rounded-2xl bg-white/70 border border-[#7D8E95]/10 animate-in slide-in-from-top-2 duration-200">
                            <SmallText className="text-[#7D8E95] mb-4 text-center">
                                Adjust any settings below. Your category's recommended defaults are pre-selected.
                            </SmallText>

                            {/* Duration Selection */}
                            <div className="mb-6">
                                <SmallText className="text-[#7D8E95] uppercase tracking-wide mb-3">Duration</SmallText>
                                <div className="grid grid-cols-3 gap-2">
                                    {DURATION_OPTIONS.map((d) => {
                                        const isSelected = targetCycles === d.cycles;
                                        const categoryDefault = SESSION_CATEGORIES[speed]?.defaults?.targetCycles;
                                        const isRecommended = d.cycles === categoryDefault;
                                        return (
                                            <button
                                                key={d.cycles}
                                                onClick={() => setTargetCycles(d.cycles)}
                                                className={cn(
                                                    'py-2 px-3 rounded-xl border-2 transition-all text-center relative',
                                                    isSelected 
                                                        ? 'border-[#1D6B6B] bg-[#1D6B6B]/10' 
                                                        : 'border-[#7D8E95]/20 bg-white/50 hover:border-[#7D8E95]/40'
                                                )}
                                                data-testid={`duration-${d.cycles}`}
                                            >
                                                {isRecommended && (
                                                    <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-1.5 py-0.5 bg-[#1D6B6B] text-white text-[10px] rounded-full whitespace-nowrap">
                                                        Best
                                                    </span>
                                                )}
                                                <BodyText className={cn('font-medium text-sm', isSelected ? 'text-[#1D6B6B]' : 'text-[#344648]')}>
                                                    {d.label}
                                                </BodyText>
                                                <SmallText className="text-[#7D8E95] text-xs">{d.time}</SmallText>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Visual Options (for visual and combined modes) */}
                            {(mode === 'visual' || mode === 'combined') && (
                                <>
                                    {/* Pattern Selection */}
                                    <div className="mb-6">
                                        <SmallText className="text-[#7D8E95] uppercase tracking-wide mb-3">Pattern</SmallText>
                                        <div className="grid grid-cols-2 gap-2">
                                            {Object.values(VISUAL_PATTERNS).map((p) => {
                                                const isSelected = pattern === p.id;
                                                const categoryDefault = SESSION_CATEGORIES[speed]?.defaults?.pattern;
                                                const isRecommended = p.id === categoryDefault;
                                                return (
                                                    <button
                                                        key={p.id}
                                                        onClick={() => setPattern(p.id)}
                                                        className={cn(
                                                            'p-2 rounded-xl border-2 transition-all text-center relative',
                                                            isSelected 
                                                                ? 'border-[#1D6B6B] bg-[#1D6B6B]/10' 
                                                                : 'border-[#7D8E95]/20 bg-white/50 hover:border-[#7D8E95]/40'
                                                        )}
                                                    >
                                                        {isRecommended && (
                                                            <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-1.5 py-0.5 bg-[#1D6B6B] text-white text-[10px] rounded-full">
                                                                Best
                                                            </span>
                                                        )}
                                                        <SmallText className={cn('font-medium', isSelected ? 'text-[#1D6B6B]' : 'text-[#7D8E95]')}>
                                                            {p.name}
                                                        </SmallText>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Color Theme */}
                                    <div className="mb-6">
                                        <SmallText className="text-[#7D8E95] uppercase tracking-wide mb-3">Color Theme</SmallText>
                                        <div className="flex gap-2 justify-center">
                                            {Object.values(COLOR_THEMES).map((t) => {
                                                const isSelected = colorTheme === t.id;
                                                return (
                                                    <button
                                                        key={t.id}
                                                        onClick={() => setColorTheme(t.id)}
                                                        className={cn(
                                                            'w-10 h-10 rounded-full border-3 transition-all shadow-md',
                                                            `bg-gradient-radial ${t.orb}`,
                                                            isSelected ? 'border-[#1D6B6B] scale-110' : 'border-white hover:scale-105'
                                                        )}
                                                        title={t.name}
                                                    />
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* Orb Size */}
                                    <div className="mb-6">
                                        <SmallText className="text-[#7D8E95] uppercase tracking-wide mb-3">Orb Size</SmallText>
                                        <div className="flex gap-2">
                                            {Object.values(SIZE_OPTIONS).map((s) => {
                                                const isSelected = orbSize === s.id;
                                                const categoryDefault = SESSION_CATEGORIES[speed]?.defaults?.orbSize;
                                                const isRecommended = s.id === categoryDefault;
                                                return (
                                                    <button
                                                        key={s.id}
                                                        onClick={() => setOrbSize(s.id)}
                                                        className={cn(
                                                            'flex-1 py-2 px-2 rounded-xl border-2 transition-all flex flex-col items-center gap-1 relative',
                                                            isSelected 
                                                                ? 'border-[#1D6B6B] bg-[#1D6B6B]/10' 
                                                                : 'border-[#7D8E95]/20 bg-white/50 hover:border-[#7D8E95]/40'
                                                        )}
                                                    >
                                                        {isRecommended && (
                                                            <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-1.5 py-0.5 bg-[#1D6B6B] text-white text-[10px] rounded-full">
                                                                Best
                                                            </span>
                                                        )}
                                                        <Circle className={cn(
                                                            'text-[#1D6B6B]',
                                                            s.id === 'small' ? 'w-3 h-3' : 
                                                            s.id === 'medium' ? 'w-4 h-4' : 
                                                            s.id === 'large' ? 'w-5 h-5' : 'w-6 h-6'
                                                        )} fill="currentColor" />
                                                        <SmallText className={cn('font-medium text-xs', isSelected ? 'text-[#1D6B6B]' : 'text-[#7D8E95]')}>
                                                            {s.name}
                                                        </SmallText>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Audio Options (for audio and combined modes) */}
                            {(mode === 'audio' || mode === 'combined') && (
                                <div className="mb-6">
                                    <SmallText className="text-[#7D8E95] uppercase tracking-wide mb-3">Audio Tone</SmallText>
                                    <div className="grid grid-cols-3 gap-2">
                                        {Object.values(AUDIO_TONES).map((t) => {
                                            const isSelected = audioTone === t.id;
                                            const categoryDefault = SESSION_CATEGORIES[speed]?.defaults?.audioTone;
                                            const isRecommended = t.id === categoryDefault;
                                            return (
                                                <button
                                                    key={t.id}
                                                    onClick={() => setAudioTone(t.id)}
                                                    className={cn(
                                                        'p-2 rounded-xl border-2 transition-all text-center relative',
                                                        isSelected 
                                                            ? 'border-[#1D6B6B] bg-[#1D6B6B]/10' 
                                                            : 'border-[#7D8E95]/20 bg-white/50 hover:border-[#7D8E95]/40'
                                                    )}
                                                >
                                                    {isRecommended && (
                                                        <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-1.5 py-0.5 bg-[#1D6B6B] text-white text-[10px] rounded-full">
                                                            Best
                                                        </span>
                                                    )}
                                                    <SmallText className={cn('font-medium text-xs', isSelected ? 'text-[#1D6B6B]' : 'text-[#7D8E95]')}>
                                                        {t.name}
                                                    </SmallText>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Guided Prompts Toggle */}
                            <div>
                                <button
                                    onClick={() => setGuidedMode(!guidedMode)}
                                    className={cn(
                                        'w-full p-3 rounded-xl flex items-center justify-between',
                                        guidedMode ? 'bg-[#1D6B6B]/10' : 'bg-[#7D8E95]/10'
                                    )}
                                >
                                    <div className="text-left">
                                        <SmallText className={cn('font-medium', guidedMode ? 'text-[#1D6B6B]' : 'text-[#7D8E95]')}>
                                            Calming Prompts
                                        </SmallText>
                                        <SmallText className="text-[#7D8E95] text-xs">
                                            Gentle reminders during session
                                        </SmallText>
                                    </div>
                                    <div className={cn(
                                        'w-5 h-5 rounded-full border-2 flex items-center justify-center',
                                        guidedMode ? 'border-[#1D6B6B] bg-[#1D6B6B]' : 'border-[#7D8E95]/40'
                                    )}>
                                        {guidedMode && <Check className="w-3 h-3 text-white" />}
                                    </div>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Start Button */}
                    <PrimaryButton
                        onClick={() => {
                            // For audio/combined mode, show headphone warning if not confirmed
                            if ((mode === 'audio' || mode === 'combined') && !headphoneConfirmed) {
                                setShowHeadphoneWarning(true);
                            } else {
                                handleStart();
                            }
                        }}
                        className="w-full"
                        size="lg"
                        data-testid="start-emdr-btn"
                    >
                        {mode === 'audio' ? (
                            <>
                                <Headphones className="w-5 h-5 mr-2" />
                                Begin Audio Session
                            </>
                        ) : mode === 'combined' ? (
                            <>
                                <Sparkles className="w-5 h-5 mr-2" />
                                Begin Combined Session
                            </>
                        ) : (
                            'Begin Session'
                        )}
                    </PrimaryButton>
                </main>

                {/* Info Modal */}
                {showInfo && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm" onClick={() => setShowInfo(false)}>
                        <div className="glass-card-elevated max-w-md w-full p-6 rounded-3xl" onClick={e => e.stopPropagation()}>
                            <div className="flex justify-between items-start mb-4">
                                <Heading as="h3" size="text-lg">About This Tool</Heading>
                                <IconButton onClick={() => setShowInfo(false)}>
                                    <X className="w-5 h-5" />
                                </IconButton>
                            </div>
                            <BodyText className="text-[#7D8E95] mb-4">
                                This bilateral stimulation tool uses gentle left-right movement to help calm your nervous system and interrupt racing thoughts.
                            </BodyText>
                            <BodyText className="text-[#7D8E95] mb-4">
                                <strong>What it's for:</strong> Relaxation, grounding, calming ADHD hyperactivity, interrupting thought spirals, and helping your brain shift from "fight or flight" to a more settled state.
                            </BodyText>
                            <BodyText className="text-[#7D8E95] mb-4">
                                <strong>How it works:</strong> The bilateral movement engages both hemispheres of your brain, which naturally promotes relaxation and helps break repetitive thought patterns.
                            </BodyText>
                            <div className="p-3 rounded-xl bg-[#FFBB98]/20 border border-white/40/30">
                                <SmallText className="text-[#7D8E95]">
                                    <strong className="text-[#E09060]">Important:</strong> This tool is designed for relaxation and grounding only. It is NOT intended for processing trauma or difficult memories. For trauma work, please consult a licensed EMDR therapist.
                                </SmallText>
                            </div>
                        </div>
                    </div>
                )}

                {/* Headphone Warning Modal */}
                {showHeadphoneWarning && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm" onClick={() => setShowHeadphoneWarning(false)}>
                        <div className="glass-card-elevated max-w-md w-full p-6 rounded-3xl" onClick={e => e.stopPropagation()}>
                            <div className="flex justify-between items-start mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-full bg-[#FFBB98]/20 flex items-center justify-center">
                                        <Headphones className="w-6 h-6 text-[#1D6B6B]" />
                                    </div>
                                    <Heading as="h3" size="text-lg">{HEADPHONE_NOTICE.title}</Heading>
                                </div>
                                <IconButton onClick={() => setShowHeadphoneWarning(false)}>
                                    <X className="w-5 h-5" />
                                </IconButton>
                            </div>
                            
                            <BodyText className="text-[#7D8E95] mb-4">
                                {HEADPHONE_NOTICE.message}
                            </BodyText>
                            
                            <div className="bg-[#7D8E95]/10 rounded-xl p-4 mb-6">
                                <SmallText className="text-[#7D8E95] font-medium mb-2">Tips for best results:</SmallText>
                                <ul className="space-y-1">
                                    {HEADPHONE_NOTICE.tips.map((tip, i) => (
                                        <li key={i} className="flex items-start gap-2">
                                            <Check className="w-4 h-4 text-[#1D6B6B] mt-0.5 flex-shrink-0" />
                                            <SmallText className="text-[#7D8E95]">{tip}</SmallText>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            
                            <div className="flex gap-3">
                                <SecondaryButton 
                                    onClick={() => setShowHeadphoneWarning(false)} 
                                    className="flex-1"
                                >
                                    Cancel
                                </SecondaryButton>
                                <PrimaryButton 
                                    onClick={() => {
                                        setHeadphoneConfirmed(true);
                                        setShowHeadphoneWarning(false);
                                        handleStart();
                                    }} 
                                    className="flex-1"
                                    data-testid="confirm-headphones-btn"
                                >
                                    <Headphones className="w-4 h-4 mr-2" />
                                    I Have Headphones
                                </PrimaryButton>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // ============ RENDER: ACTIVE PHASE ============
    if (sessionPhase === 'active') {
        const progressPercent = (currentCycle / targetCycles) * 100;

        return (
            <div className={cn('min-h-screen', mode === 'audio' ? 'bg-[#1a1a2e]' : COLOR_THEMES[colorTheme].bg)}>
                {/* Feedback Modal */}
                <ToolFeedback
                    isOpen={showFeedback}
                    onClose={handleFeedbackClose}
                    toolId={`emdr-${mode}-${pattern}`}
                    toolName={`EMDR/Bilateral Regulation (${mode === 'audio' ? 'Audio' : mode === 'combined' ? 'Combined' : VISUAL_PATTERNS[pattern]?.name})`}
                />

                {/* Minimal Header */}
                <header className="absolute top-0 left-0 right-0 z-[70] p-4">
                    <div className="flex items-center justify-between">
                        <IconButton 
                            onClick={handleReset}
                            className="bg-white/20 backdrop-blur-sm"
                            data-testid="reset-btn"
                        >
                            <X className="w-5 h-5" />
                        </IconButton>
                        <SmallText className={cn(
                            'font-medium',
                            mode === 'audio' ? 'text-white/80' : 'text-[#7D8E95]'
                        )}>
                            {currentCycle} / {targetCycles} cycles
                        </SmallText>
                        <IconButton 
                            onClick={() => setShowSettings(!showSettings)}
                            className="bg-white/20 backdrop-blur-sm"
                            data-testid="settings-btn"
                        >
                            <Settings className="w-5 h-5" />
                        </IconButton>
                    </div>

                    {/* Progress bar */}
                    <div className="mt-4 h-1 bg-white/20 rounded-full overflow-hidden">
                        <div 
                            className="h-full bg-[#1D6B6B] transition-all duration-500"
                            style={{ width: `${progressPercent}%` }}
                        />
                    </div>
                </header>

                {/* Main Content - Maximized for visual area */}
                <main className="min-h-screen flex flex-col items-center justify-center px-2 pt-20 pb-28">
                    {/* Guided Prompt - compact, overlaid style */}
                    {guidedMode && currentPrompt && (
                        <div className="mb-4 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm animate-in fade-in duration-500">
                            <SmallText className={cn(
                                'italic text-center',
                                mode === 'audio' ? 'text-white/70' : 'text-[#2A8585]/80'
                            )}>
                                {currentPrompt}
                            </SmallText>
                        </div>
                    )}

                    {/* BLS Component */}
                    {mode === 'visual' ? (
                        <VisualBLSEnhanced
                            isActive={isPlaying}
                            pattern={pattern}
                            speed={speed}
                            colorTheme={colorTheme}
                            orbSize={orbSize}
                            onCycleComplete={handleCycleComplete}
                        />
                    ) : mode === 'combined' ? (
                        <>
                            {/* Visual component with position reporting for audio sync */}
                            <VisualBLSEnhanced
                                isActive={isPlaying}
                                pattern={pattern}
                                speed={speed}
                                colorTheme={colorTheme}
                                orbSize={orbSize}
                                onCycleComplete={handleCycleComplete}
                                onPositionChange={setOrbPosition}
                            />
                            {/* Synced audio component - receives position from visual */}
                            <SyncedAudio
                                isActive={isPlaying}
                                orbPosition={orbPosition}
                                speed={speed}
                                toneType={audioTone}
                                volume={audioVolume}
                            />
                            {/* Headphones indicator for combined mode */}
                            <div className="absolute bottom-32 left-1/2 -translate-x-1/2 flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm">
                                <Headphones className="w-3 h-3 text-[#1D6B6B]" />
                                <SmallText className="text-[#7D8E95]/70 text-xs">Audio synced</SmallText>
                            </div>
                        </>
                    ) : (
                        <AudioBLS
                            isActive={isPlaying}
                            speed={speed}
                            toneType={audioTone}
                            volume={audioVolume}
                            onCycleComplete={handleCycleComplete}
                        />
                    )}

                    {/* Instructions - minimal, only show briefly or on pause */}
                    {!isPlaying && (
                        <SmallText className={cn(
                            'mt-4 text-center max-w-sm',
                            mode === 'audio' ? 'text-white/50' : 'text-[#7D8E95]/70'
                        )}>
                            {mode === 'visual' 
                                ? 'Follow the light with your eyes. Keep your head still.'
                                : mode === 'combined'
                                ? 'Follow the light. The tones will sync with the movement.'
                                : 'Close your eyes. Let the tones guide you.'
                            }
                        </SmallText>
                    )}
                </main>

                {/* Controls - compact, less intrusive */}
                <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/10 to-transparent">
                    <div className="max-w-sm mx-auto flex items-center justify-center gap-3">
                        <SecondaryButton
                            onClick={handleReset}
                            size="sm"
                            className="px-4"
                            data-testid="stop-btn"
                        >
                            <RotateCcw className="w-4 h-4" />
                        </SecondaryButton>
                        <button
                            onClick={handleTogglePlay}
                            className={cn(
                                'w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all',
                                isPlaying 
                                    ? 'bg-[#FFBB98] hover:bg-[#E09060]' 
                                    : 'bg-[#1D6B6B] hover:bg-[#155555]'
                            )}
                            data-testid="play-pause-btn"
                        >
                            {isPlaying ? (
                                <Pause className="w-5 h-5 text-white" />
                            ) : (
                                <Play className="w-5 h-5 text-white ml-0.5" />
                            )}
                        </button>
                        <PrimaryButton
                            onClick={handleComplete}
                            size="sm"
                            className="px-4"
                            data-testid="done-btn"
                        >
                            Done
                        </PrimaryButton>
                    </div>
                </div>

                {/* Settings Panel */}
                {showSettings && (
                    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/30 backdrop-blur-sm" onClick={() => setShowSettings(false)}>
                        <div 
                            className="w-full max-w-lg bg-white rounded-t-3xl p-6 animate-in slide-in-from-bottom"
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="flex justify-between items-center mb-6">
                                <Heading as="h3" size="text-lg">Quick Settings</Heading>
                                <IconButton onClick={() => setShowSettings(false)}>
                                    <X className="w-5 h-5" />
                                </IconButton>
                            </div>

                            {/* Speed */}
                            <div className="mb-4">
                                <SmallText className="text-[#7D8E95] mb-2">Session Type</SmallText>
                                <div className="grid grid-cols-2 gap-2">
                                    {Object.values(SESSION_CATEGORIES).map((cat) => (
                                        <button
                                            key={cat.id}
                                            onClick={() => setSpeed(cat.id)}
                                            className={cn(
                                                'py-2 px-3 rounded-lg text-sm transition-colors text-left',
                                                speed === cat.id ? 'bg-[#1D6B6B] text-white' : 'bg-[#7D8E95]/10 text-[#7D8E95]'
                                            )}
                                        >
                                            <div className="font-medium">{cat.name}</div>
                                            <div className="text-xs opacity-70">{cat.bpm} BPM</div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Orb Size (visual only) */}
                            {mode === 'visual' && (
                                <div className="mb-4">
                                    <SmallText className="text-[#7D8E95] mb-2">Orb Size</SmallText>
                                    <div className="flex gap-2">
                                        {Object.values(SIZE_OPTIONS).map((s) => (
                                            <button
                                                key={s.id}
                                                onClick={() => setOrbSize(s.id)}
                                                className={cn(
                                                    'flex-1 py-2 rounded-lg text-sm transition-colors flex flex-col items-center gap-1',
                                                    orbSize === s.id ? 'bg-[#1D6B6B] text-white' : 'bg-[#7D8E95]/10 text-[#7D8E95]'
                                                )}
                                            >
                                                <Circle className={cn(
                                                    s.id === 'small' ? 'w-3 h-3' : 
                                                    s.id === 'medium' ? 'w-4 h-4' : 
                                                    s.id === 'large' ? 'w-5 h-5' : 'w-6 h-6'
                                                )} fill="currentColor" />
                                                <span>{s.name}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Audio Tone (audio only) */}
                            {mode === 'audio' && (
                                <div className="mb-4">
                                    <SmallText className="text-[#7D8E95] mb-2">Audio Tone</SmallText>
                                    <div className="grid grid-cols-2 gap-2">
                                        {Object.values(AUDIO_TONES).map((t) => (
                                            <button
                                                key={t.id}
                                                onClick={() => setAudioTone(t.id)}
                                                className={cn(
                                                    'py-2 px-3 rounded-lg text-sm transition-colors text-left',
                                                    audioTone === t.id ? 'bg-[#1D6B6B] text-white' : 'bg-[#7D8E95]/10 text-[#7D8E95]'
                                                )}
                                            >
                                                {t.name}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Volume (audio only) */}
                            {mode === 'audio' && (
                                <div className="mb-4">
                                    <SmallText className="text-[#7D8E95] mb-2">Volume: {Math.round(audioVolume * 100)}%</SmallText>
                                    <input
                                        type="range"
                                        min="0"
                                        max="1"
                                        step="0.1"
                                        value={audioVolume}
                                        onChange={(e) => setAudioVolume(parseFloat(e.target.value))}
                                        className="w-full accent-[#1D6B6B]"
                                    />
                                </div>
                            )}

                            {/* Guided mode */}
                            <button
                                onClick={() => setGuidedMode(!guidedMode)}
                                className={cn(
                                    'w-full p-3 rounded-xl flex items-center justify-between',
                                    guidedMode ? 'bg-[#1D6B6B]/10' : 'bg-[#7D8E95]/10'
                                )}
                            >
                                <span className="text-[#7D8E95]">Guided prompts</span>
                                <div className={cn(
                                    'w-5 h-5 rounded-full border-2 flex items-center justify-center',
                                    guidedMode ? 'border-[#1D6B6B] bg-[#1D6B6B]' : 'border-[#7D8E95]/40'
                                )}>
                                    {guidedMode && <Check className="w-3 h-3 text-white" />}
                                </div>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // ============ RENDER: COMPLETE PHASE ============
    if (sessionPhase === 'complete') {
        return (
            <div className="min-h-screen bg-[#F5F0E8] flex items-center justify-center px-6">
                {/* Feedback Modal */}
                <ToolFeedback
                    isOpen={showFeedback}
                    onClose={handleFeedbackClose}
                    toolId={`emdr-${mode}-${pattern}`}
                    toolName={`EMDR/Bilateral Regulation (${mode === 'audio' ? 'Audio' : mode === 'combined' ? 'Combined' : VISUAL_PATTERNS[pattern]?.name})`}
                />

                <div className="max-w-sm w-full text-center">
                    <div className="w-20 h-20 rounded-full bg-[#1D6B6B]/10 flex items-center justify-center mx-auto mb-6">
                        <Sparkles className="w-10 h-10 text-[#1D6B6B]" />
                    </div>
                    <Heading as="h2" size="text-2xl" className="mb-3">Nicely Done</Heading>
                    <BodyText className="text-[#7D8E95] mb-2">
                        You completed {targetCycles} cycles of calming bilateral movement.
                    </BodyText>
                    <SmallText className="text-[#7D8E95] mb-8">
                        Notice if your mind feels a bit quieter, your body a bit more relaxed.
                    </SmallText>

                    <div className="space-y-3">
                        <PrimaryButton 
                            onClick={() => setShowFeedback(true)} 
                            className="w-full" 
                            size="lg"
                            data-testid="feedback-btn"
                        >
                            How did that feel?
                        </PrimaryButton>
                        <SecondaryButton 
                            onClick={() => navigate('/home')} 
                            className="w-full" 
                            size="lg"
                            data-testid="return-home-btn"
                        >
                            Return Home
                        </SecondaryButton>
                        <button 
                            onClick={handleReset}
                            className="text-sm text-[#7D8E95] hover:text-[#1D6B6B] transition-colors"
                        >
                            Start another session
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return null;
};

export default EMDR;
